/**
 * Keyboard Shortcuts Registry
 *
 * Central registry of all keyboard shortcuts in the application.
 * Add new shortcuts here to keep them organized and avoid conflicts.
 */

import type { ShortcutDefinition, KeyDisplay } from './types'

/**
 * Detect if user is on macOS
 */
export function isMac(): boolean {
  if (typeof navigator === 'undefined') return false
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0
}

/**
 * Get platform-aware display string for a shortcut
 */
export function getShortcutDisplay(shortcut: ShortcutDefinition): KeyDisplay {
  const mac = isMac()
  const parts: string[] = []
  const labelParts: string[] = []

  for (const mod of shortcut.modifiers) {
    switch (mod) {
      case 'ctrl':
        parts.push(mac ? '⌃' : 'Ctrl')
        labelParts.push('Control')
        break
      case 'meta':
        parts.push(mac ? '⌘' : 'Ctrl')
        labelParts.push(mac ? 'Command' : 'Control')
        break
      case 'alt':
        parts.push(mac ? '⌥' : 'Alt')
        labelParts.push(mac ? 'Option' : 'Alt')
        break
      case 'shift':
        parts.push(mac ? '⇧' : 'Shift')
        labelParts.push('Shift')
        break
    }
  }

  // Format key for display
  const keyDisplay = shortcut.key.length === 1
    ? shortcut.key.toUpperCase()
    : formatKeyName(shortcut.key)

  parts.push(keyDisplay)
  labelParts.push(keyDisplay)

  return {
    display: mac ? parts.join('') : parts.join('+'),
    label: labelParts.join(' '),
  }
}

/**
 * Format special key names for display
 */
function formatKeyName(key: string): string {
  const keyMap: Record<string, string> = {
    escape: 'Esc',
    enter: 'Enter',
    arrowup: '↑',
    arrowdown: '↓',
    arrowleft: '←',
    arrowright: '→',
    backspace: '⌫',
    delete: 'Del',
    tab: 'Tab',
    space: 'Space',
    '/': '/',
    '?': '?',
    '[': '[',
    ']': ']',
  }
  return keyMap[key.toLowerCase()] || key
}

/**
 * All registered shortcuts in the application
 *
 * IMPORTANT: Add new shortcuts here to maintain a single source of truth.
 * This enables conflict detection and centralized documentation.
 */
export const SHORTCUTS = {
  // ============================================
  // Navigation Shortcuts
  // ============================================
  GLOBAL_SEARCH: {
    id: 'global-search',
    description: 'Open global search',
    key: 'k',
    modifiers: ['meta'],
    category: 'navigation',
    preventDefault: true,
  },

  GO_TO_DASHBOARD: {
    id: 'go-to-dashboard',
    description: 'Go to dashboard',
    key: 'h',
    modifiers: ['meta', 'shift'],
    category: 'navigation',
    preventDefault: true,
  },

  GO_BACK: {
    id: 'go-back',
    description: 'Go back',
    key: '[',
    modifiers: ['meta'],
    category: 'navigation',
    preventDefault: true,
  },

  GO_FORWARD: {
    id: 'go-forward',
    description: 'Go forward',
    key: ']',
    modifiers: ['meta'],
    category: 'navigation',
    preventDefault: true,
  },

  LIST_NEXT: {
    id: 'list-next',
    description: 'Next item in list',
    key: 'j',
    modifiers: [],
    category: 'navigation',
    preventDefault: false,
  },

  LIST_PREV: {
    id: 'list-prev',
    description: 'Previous item in list',
    key: 'k',
    modifiers: [],
    category: 'navigation',
    preventDefault: false,
  },

  LIST_OPEN: {
    id: 'list-open',
    description: 'Open selected item',
    key: 'enter',
    modifiers: [],
    category: 'navigation',
    preventDefault: false,
  },

  // ============================================
  // General Shortcuts
  // ============================================
  SHOW_KEYBOARD_HELP: {
    id: 'show-keyboard-help',
    description: 'Show keyboard shortcuts',
    key: '/',
    modifiers: ['meta'],
    category: 'general',
    preventDefault: true,
  },

  CLOSE_DIALOG: {
    id: 'close-dialog',
    description: 'Close dialog / Cancel',
    key: 'escape',
    modifiers: [],
    category: 'general',
    preventDefault: false,
  },

  // ============================================
  // Action Shortcuts
  // ============================================
  SAVE: {
    id: 'save',
    description: 'Save current item',
    key: 's',
    modifiers: ['meta'],
    category: 'actions',
    preventDefault: true,
  },

  NEW_ITEM: {
    id: 'new-item',
    description: 'Create new item',
    key: 'n',
    modifiers: ['meta'],
    category: 'actions',
    preventDefault: true,
  },

  SUBMIT_FORM: {
    id: 'submit-form',
    description: 'Submit form / Send message',
    key: 'enter',
    modifiers: ['meta'],
    category: 'actions',
    preventDefault: true,
  },

  DELETE_ITEM: {
    id: 'delete-item',
    description: 'Delete selected item',
    key: 'backspace',
    modifiers: ['meta'],
    category: 'actions',
    preventDefault: true,
  },

  DUPLICATE_ITEM: {
    id: 'duplicate-item',
    description: 'Duplicate item',
    key: 'd',
    modifiers: ['meta', 'shift'],
    category: 'actions',
    preventDefault: true,
  },

  // ============================================
  // Editing Shortcuts
  // ============================================
  TOGGLE_BOLD: {
    id: 'toggle-bold',
    description: 'Toggle bold text',
    key: 'b',
    modifiers: ['meta'],
    category: 'editing',
    preventDefault: true,
  },

  TOGGLE_ITALIC: {
    id: 'toggle-italic',
    description: 'Toggle italic text',
    key: 'i',
    modifiers: ['meta'],
    category: 'editing',
    preventDefault: true,
  },

  // ============================================
  // CMS / Editor Shortcuts
  // ============================================
  PUBLISH: {
    id: 'publish',
    description: 'Publish page',
    key: 'p',
    modifiers: ['meta', 'shift'],
    category: 'cms',
    preventDefault: true,
  },

  TOGGLE_PREVIEW: {
    id: 'toggle-preview',
    description: 'Toggle preview mode',
    key: 'p',
    modifiers: ['meta'],
    category: 'cms',
    preventDefault: true,
  },

  EXIT_PREVIEW: {
    id: 'exit-preview',
    description: 'Exit preview mode',
    key: 'escape',
    modifiers: [],
    category: 'cms',
    preventDefault: false,
  },
} as const satisfies Record<string, ShortcutDefinition>

/**
 * Get shortcuts grouped by category
 */
export function getShortcutsByCategory(): Record<string, ShortcutDefinition[]> {
  const grouped: Record<string, ShortcutDefinition[]> = {
    general: [],
    navigation: [],
    actions: [],
    editing: [],
    cms: [],
  }

  for (const shortcut of Object.values(SHORTCUTS)) {
    // Check if shortcut is enabled (undefined means enabled)
    if (!('enabled' in shortcut) || shortcut.enabled !== false) {
      const category = grouped[shortcut.category]
      if (category) {
        category.push(shortcut as ShortcutDefinition)
      }
    }
  }

  return grouped
}

/**
 * Category display names
 */
export const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  navigation: 'Navigation',
  actions: 'Actions',
  editing: 'Editing',
  cms: 'Page Editor',
}

/**
 * Check if an event matches a shortcut definition
 */
export function matchesShortcut(
  event: KeyboardEvent,
  shortcut: ShortcutDefinition
): boolean {
  // Check key (case-insensitive)
  if (event.key.toLowerCase() !== shortcut.key.toLowerCase()) {
    return false
  }

  // Check modifiers
  const requiredMeta = shortcut.modifiers.includes('meta')
  const requiredCtrl = shortcut.modifiers.includes('ctrl')
  const requiredAlt = shortcut.modifiers.includes('alt')
  const requiredShift = shortcut.modifiers.includes('shift')

  // Meta maps to Cmd on Mac, Ctrl on Windows/Linux
  // For cross-platform shortcuts, we accept either metaKey OR ctrlKey when 'meta' is specified
  const metaOrCtrl = event.metaKey || event.ctrlKey

  if (requiredMeta && !metaOrCtrl) return false
  if (requiredCtrl && !event.ctrlKey) return false
  if (requiredAlt && !event.altKey) return false
  if (requiredShift && !event.shiftKey) return false

  // Ensure no extra modifiers are pressed (except for meta/ctrl interchangeability)
  if (!requiredMeta && !requiredCtrl && (event.metaKey || event.ctrlKey)) return false
  if (!requiredAlt && event.altKey) return false
  if (!requiredShift && event.shiftKey) return false

  return true
}
